import React from "react";
import {
  Document,
  Image,
  Link,
  Page,
  PDFDownloadLink,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import moment from "moment"; // import "./pdfPolyfill"; // Import the polyfill to fix build error
import emailIcon from "./ResumeIcons/email.png";
import calendarIcon from "./ResumeIcons/calendar.png";
import certificateIcon from "./ResumeIcons/certificate.png";
import langIcon from "./ResumeIcons/language.png";
import locationIcon from "./ResumeIcons/location.png";
import phoneIcon from "./ResumeIcons/phone.png";
import profileIcon from "./ResumeIcons/profile.png";
import rewardIcon from "./ResumeIcons/reward.png";
import skillIcon from "./ResumeIcons/skill.png";
import workIcon from "./ResumeIcons/workHistory.png";
import portfolioIcon from "./ResumeIcons/portfolio.png";
import referencesIcon from "./ResumeIcons/references.png";
import educationIcon from "./ResumeIcons/edu.png";
import objectiveIcon from "./ResumeIcons/objective.png";
import userIcon from "./ResumeIcons/user.png";
import linkedinIcon from "./ResumeIcons/linkedin.png";
import expIcon from "./ResumeIcons/workExp.png";
import licenseIcon from "./ResumeIcons/license.png"; // Enhanced styles with blue theme matching the resume design

// Enhanced styles with blue theme matching the resume design
const styles = StyleSheet.create({
  page: {
    backgroundColor: "#ffffff",
    padding: 0,
    paddingBottom: 15,
    paddingTop: 5, // Reduced top padding to accommodate company header
    fontFamily: "Helvetica",
  },
  icon: {
    width: 16,
    height: 16,
  },

  // Company Header Styles - Compact design
  companyHeader: {
    backgroundColor: "#ffffff",
    borderBottomWidth: 1,
    borderBottomColor: "#E8F4FD",
    paddingHorizontal: 20,
    paddingVertical: 8,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    top: 0,
    left: 10,
    right: 0,
    zIndex: 10,
    marginBottom: 10,
  },
  companyLogoContainer: {
    flexDirection: "row",
    flex: 1,
    alignItems: "center",
    justifyContent: "space-between",
  },
  companyLogo: {
    width: 40,
    height: 40,
    marginRight: 8,
    objectFit: "contain",
    objectPosition: "left center",
  },
  companyName: {
    fontSize: 12,
    color: "#a4a6ac",
    marginRight: 8,
  },

  // Header Styles - Blue theme (adjusted for company header)
  header: {
    backgroundColor: "#f0f7ff",
    padding: 25,
    marginBottom: 0,
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 15,
    color: "black",
  },
  profileCircle: {
    width: 40,
    height: 40,
    borderRadius: 25,
    marginBottom: 15,
    backgroundColor: "rgb(199, 209, 220)",
    marginRight: 15,
    justifyContent: "center",
    alignItems: "center",
  },
  profileIconHeader: {
    fontSize: 20,
    color: "#ffff",
    textAlign: "center",
  },
  nameSection: {
    flex: 1,
  },
  name: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#4A90E2",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 12,
    color: "#E8F4FD",
  },
  contactGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginLeft: 10,
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 15,
    marginBottom: 4,
  },
  contactIcon: {
    width: 12,
    height: 12,
    marginRight: 6,
  },
  contactText: {
    marginLeft: 5,
    fontSize: 10,
    color: "black",
  },
  contactLink: {
    fontSize: 10,
    color: "rgb(18, 140, 234)",
    textDecoration: "none",
    marginLeft: 5,
  },

  // Content Styles (adjusted for company header)
  content: {
    padding: 25,
    paddingTop: 10, // Reduced padding since header has margin now
  },

  // Section Styles with improved page break handling
  section: {
    marginBottom: 20,
    break: "avoid", // Prevent breaking entire sections
    orphans: 2, // Minimum 2 lines at bottom of page
    widows: 2, // Minimum 2 lines at top of page
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: "#E8F4FD",
    break: "avoid", // Keep header with content
  },
  sectionIcon: {
    width: 20,
    height: 20,
    marginRight: 10,
    justifyContent: "center",
    alignItems: "center",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#4A90E2",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },

  // Objective Section
  objectiveContainer: {
    backgroundColor: "#F8FBFF",
    padding: 15,
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: "#4A90E2",
    break: "avoid",
  },
  objectiveText: {
    fontSize: 11,
    color: "#333333",
    lineHeight: 1.5,
    textAlign: "justify",
  },

  // Experience Overview Cards
  experienceGrid: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 15,
    break: "avoid",
  },
  experienceCard: {
    flex: 1,
    backgroundColor: "#F8FBFF",
    border: "1px solid #E8F4FD",
    borderRadius: 6,
    padding: 12,
    break: "avoid",
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  cardIcon: {
    width: 16,
    height: 16,
    backgroundColor: "#4A90E2",
    borderRadius: 3,
    marginRight: 8,
  },
  cardTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#333333",
  },
  cardValue: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#4A90E2",
    marginBottom: 4,
  },
  cardSubtext: {
    fontSize: 9,
    color: "#666666",
  },

  // Work History Styles with strict page break control
  jobContainer: {
    backgroundColor: "#ffffff",
    border: "1px solid #F0F0F0",
    borderRadius: 6,
    padding: 15,
    marginBottom: 12,
    break: "avoid", // Prevent the entire container from breaking
  },
  jobHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 10,
    break: "avoid", // Keep header together
  },
  jobTitleSection: {
    flex: 1,
  },
  jobTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
  },
  jobIcon: {
    width: 14,
    height: 14,
    backgroundColor: "#4A90E2",
    borderRadius: 3,
    marginRight: 8,
  },
  jobTitle: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#333333",
  },
  companyRow: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: 22,
  },
  companyText: {
    fontSize: 10,
    color: "#666666",
    fontWeight: "600",
  },
  locationText: {
    fontSize: 10,
    color: "#999999",
    marginLeft: 5,
  },
  dateContainer: {
    backgroundColor: "#F8FBFF",
    padding: 6,
    borderRadius: 4,
    border: "1px solid #E8F4FD",
  },
  dateText: {
    fontSize: 9,
    color: "#4A90E2",
    fontWeight: "600",
  },
  responsibilitiesList: {
    marginLeft: 22,
    marginTop: 8,
    break: "avoid", // Keep responsibilities together
  },
  responsibilityItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 5,
    break: "avoid", // Keep individual responsibility items together
  },
  bullet: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: "#4A90E2",
    marginRight: 8,
    marginTop: 5,
  },
  responsibilityText: {
    fontSize: 10,
    color: "#555555",
    lineHeight: 1.4,
    flex: 1,
  },

  // Education Styles with page break control
  educationContainer: {
    backgroundColor: "#ffffff",
    border: "1px solid #F0F0F0",
    borderRadius: 6,
    padding: 15,
    marginBottom: 12,
    break: "avoid", // Prevent breaking education entries
  },
  educationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    break: "avoid",
  },
  educationContent: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  educationIcon: {
    width: 14,
    height: 14,
    backgroundColor: "#4A90E2",
    borderRadius: 3,
    marginRight: 10,
  },
  degreeTitle: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#333333",
    marginBottom: 3,
  },
  institutionText: {
    fontSize: 10,
    color: "#666666",
    fontWeight: "550",
  },

  // Skills and Other Sections
  skillsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    break: "avoid",
  },
  skillChip: {
    backgroundColor: "#eff6ff",
    color: "#2563EB",
    borderRadius: 30,
    padding: 5,
    fontSize: 7,
    fontWeight: "500",
    border: "1px solid #bfdbfe",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  tagChip: {
    backgroundColor: "#ecfdf5",
    color: "#10b981",
    borderRadius: 30,
    padding: 5,
    fontSize: 7,
    fontWeight: "500",
    border: "1px solid #a7f3d0",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  languageChip: {
    backgroundColor: "#FFF8E1",
    color: "#F57C00",
    borderRadius: 30,
    padding: 5,
    fontSize: 7,
    fontWeight: "500",
    border: "1px solid #FFE0B2",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },

  // Certification Styles (Orange Theme)
  certContainer: {
    backgroundColor: "#FFFBF0",
    border: "1px solid #FFE0B2",
    borderRadius: 6,
    padding: 12,
    marginBottom: 10,
    break: "avoid", // Keep certifications together
  },
  certTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "rgb(72, 49, 36)",
    marginBottom: 5,
  },
  certDetails: {
    fontSize: 9,
    color: "rgb(191, 104, 12)",
    marginBottom: 2,
  },

  // License Styles (Blue Theme)
  licenseContainer: {
    backgroundColor: "#F0F8FF",
    border: "1px solid #B3D9FF",
    borderRadius: 6,
    padding: 12,
    marginBottom: 10,
    break: "avoid", // Keep licenses together
  },
  licenseTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#1565C0",
    marginBottom: 5,
  },
  licenseDetails: {
    fontSize: 9,
    color: "#1976D2",
    marginBottom: 2,
  },

  // Portfolio and Awards Styles (Purple Theme)
  portfolioContainer: {
    backgroundColor: "rgb(248, 240, 250)",
    border: "1px solidrgb(221, 206, 224)",
    borderRadius: 6,
    padding: 12,
    marginBottom: 10,
    break: "avoid", // Keep portfolio items together
  },
  portfolioTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "rgb(76, 38, 92)",
    marginBottom: 5,
  },
  portfolioLink: {
    fontSize: 9,
    color: "#8E24AA",
    textDecoration: "none",
  },

  // References Styles (Green Theme)
  referenceContainer: {
    backgroundColor: "#ecfdf5",
    border: "1px solid #A5D6A7",
    borderRadius: 6,
    padding: 12,
    marginBottom: 10,
    break: "avoid", // Keep references together
  },
  referenceName: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#2E7D32",
    marginBottom: 3,
  },
  referenceDetails: {
    fontSize: 9,
    color: "#388E3C",
    marginBottom: 2,
  },
});

// Enhanced SectionIcon component with proper icon mapping
const SectionIcon = ({ name }) => {
  const icons = {
    objective: objectiveIcon,
    experience: workIcon,
    work: expIcon,
    education: educationIcon,
    skills: skillIcon,
    languages: langIcon,
    certifications: certificateIcon,
    references: referencesIcon,
    portfolio: portfolioIcon,
    email: emailIcon,
    phone: phoneIcon,
    location: locationIcon,
    calendar: calendarIcon,
    profile: profileIcon,
    user: userIcon,
    linkedin: linkedinIcon,
    reward: rewardIcon,
    license: licenseIcon,
  };

  const iconSrc = icons[name];
  return iconSrc ? <Image style={styles.icon} src={iconSrc} /> : null;
};

// Helper function to ensure URLs have proper format
const formatPdfUrl = (url) => {
  if (!url) return "";
  return url.startsWith("http://") || url.startsWith("https://") ? url : `https://${url}`;
};

// Enhanced LicensesSection with consistent styling and page break handling
export const LicensesSection = ({ licenses }) => {
  if (!licenses?.length) return null;

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader} wrap={false}>
        <View style={styles.sectionIcon}>
          <SectionIcon name="license" />
        </View>
        <Text style={styles.sectionTitle}>Licenses</Text>
      </View>
      {licenses.map((license) => (
        <View
          key={license.id || `license-${license.name}`}
          style={styles.licenseContainer}
          minPresenceAhead={40}
          wrap={false}
        >
          <Text style={styles.licenseTitle}>{license.name}</Text>
          {license.licenseStatus && (
            <Text style={styles.licenseDetails}>Status: {license.licenseStatus}</Text>
          )}
          {license.licenseNumber && (
            <Text style={styles.licenseDetails}>License #: {license.licenseNumber}</Text>
          )}
          {license.issuingAuthority && (
            <Text style={styles.licenseDetails}>Issued by: {license.issuingAuthority}</Text>
          )}
          {license.issueDate && (
            <Text style={styles.licenseDetails}>
              Issue Date: {moment(license.issueDate).format("MMM YYYY")}
            </Text>
          )}
          {license.expiryDate && (
            <Text style={styles.licenseDetails}>
              Expires: {moment(license.expiryDate).format("MMM YYYY")}
            </Text>
          )}
        </View>
      ))}
    </View>
  );
};

// Enhanced AwardsSection with consistent styling and page break handling
export const AwardsSection = ({ awards }) => {
  if (!awards?.length) return null;

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader} wrap={false}>
        <View style={styles.sectionIcon}>
          <SectionIcon name="reward" />
        </View>
        <Text style={styles.sectionTitle}>Awards & Recognition</Text>
      </View>
      {awards.map((award) => (
        <View
          key={award.id || `award-${award.name}`}
          style={styles.portfolioContainer}
          minPresenceAhead={40}
          wrap={false}
        >
          <Text style={styles.portfolioTitle}>{award.name}</Text>
          {award.description && <Text style={styles.certDetails}>{award.description}</Text>}
          {award.issuer && <Text style={styles.certDetails}>Awarded by: {award.issuer}</Text>}
          {Number(award.year) > 0 && moment(award.year, "YYYY").isValid() && (
            <Text style={styles.certDetails}>Year: {award.year}</Text>
          )}
          {award.category && <Text style={styles.certDetails}>Category: {award.category}</Text>}
          {award.level && <Text style={styles.certDetails}>Level: {award.level}</Text>}
        </View>
      ))}
    </View>
  );
};

// Company Header Component
const CompanyHeader = ({ companyName, companyLogo }) => {
  // if (!companyLogo) return null;

  return (
    <View style={styles.companyHeader} fixed wrap={false}>
      <View style={styles.companyLogoContainer}>
        {companyLogo && <Image style={styles.companyLogo} src={companyLogo} />}
        <Text style={styles.companyName}>{companyName}</Text>
      </View>
    </View>
  );
};

// Enhanced PDF Document Component
const ResumePDF = ({ resumeData, companyName, companyLogo }) => {
  if (!resumeData) return null;

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <CompanyHeader companyName={companyName} companyLogo={companyLogo} />
        {/* Enhanced Header Section with proper contact icons */}
        {resumeData.contactInfo && (
          <View style={styles.header}>
            <View style={styles.headerContent}>
              <View style={styles.profileCircle}>
                <SectionIcon name="profile" />
              </View>
              <View style={styles.nameSection}>
                {(resumeData.contactInfo.firstName || resumeData.contactInfo.lastName) && (
                  <Text style={styles.name}>
                    {[resumeData.contactInfo.firstName, resumeData.contactInfo.lastName]
                      .filter(Boolean)
                      .join(" ")}
                  </Text>
                )}
                {/* Location moved here, right under the name */}
                {(resumeData.contactInfo.addressLineOne ||
                  resumeData.contactInfo.city ||
                  resumeData.contactInfo.state ||
                  resumeData.contactInfo.zipCode) && (
                  <View style={[styles.contactItem, { marginLeft: 0, marginBottom: 6 }]}>
                    <SectionIcon name="location" />
                    <Text style={styles.contactText}>
                      {resumeData.contactInfo.addressLineOne &&
                        `${resumeData.contactInfo.addressLineOne}, `}
                      {[
                        resumeData.contactInfo.city,
                        resumeData.contactInfo.state,
                        resumeData.contactInfo.zipCode,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </Text>
                  </View>
                )}
                <Text style={styles.subtitle}>Professional Resume</Text>
              </View>
            </View>

            <View style={styles.contactGrid}>
              {resumeData.contactInfo.email && (
                <View style={styles.contactItem}>
                  <SectionIcon name="email" />
                  <Text style={styles.contactText}>{resumeData.contactInfo.email}</Text>
                </View>
              )}

              {resumeData.contactInfo.phone && (
                <View style={styles.contactItem}>
                  <SectionIcon name="phone" />
                  <Text style={styles.contactText}>{resumeData.contactInfo.phone}</Text>
                </View>
              )}

              {resumeData.contactInfo.linkedin && (
                <View style={styles.contactItem}>
                  <SectionIcon name="linkedin" />
                  <Link
                    href={formatPdfUrl(resumeData.contactInfo.linkedin)}
                    src={formatPdfUrl(resumeData.contactInfo.linkedin)}
                    style={styles.contactLink}
                  >
                    {resumeData.contactInfo.linkedin.replace(/^https?:\/\/(www\.)?/, "")}
                  </Link>
                </View>
              )}
            </View>
          </View>
        )}

        <View style={styles.content}>
          {/* Enhanced Objective Section */}
          {resumeData.objective && (
            <View style={styles.section} minPresenceAhead={20}>
              <View style={styles.sectionHeader}>
                <View style={styles.sectionIcon}>
                  <SectionIcon name="objective" />
                </View>
                <Text style={styles.sectionTitle}>Career Objective</Text>
              </View>
              <View style={styles.objectiveContainer}>
                <Text style={styles.objectiveText}>{resumeData.objective}</Text>
              </View>
            </View>
          )}

          {/* Enhanced Experience Overview */}
          {resumeData.overallExperience &&
            (resumeData.overallExperience.years || resumeData.overallExperience.months) && (
              <View style={styles.section}>
                <View style={styles.sectionHeader} wrap={false}>
                  <View style={styles.sectionIcon}>
                    <SectionIcon name="experience" />
                  </View>
                  <Text style={styles.sectionTitle}>Experience Overview</Text>
                </View>

                <View style={styles.experienceGrid}>
                  <View style={styles.experienceCard}>
                    <View style={styles.cardHeader}>
                      <View style={styles.cardIcon} />
                      <Text style={styles.cardTitle}>Overall Experience</Text>
                    </View>
                    <Text style={styles.cardValue}>{resumeData.overallExperience.years} Years</Text>
                    <Text style={styles.cardSubtext}>Professional Experience</Text>
                  </View>

                  {resumeData.industries && resumeData.industries.length > 0 && (
                    <>
                      {resumeData.industries.slice(0, 2).map((industry, index) => (
                        <View
                          key={industry.industryName || `industry-${index}`}
                          style={styles.experienceCard}
                          wrap={false}
                        >
                          <View style={styles.cardHeader}>
                            <View style={styles.cardIcon} />
                            <Text style={styles.cardTitle}>{industry.industryName}</Text>
                          </View>
                          {industry.yearsOfExperience && (
                            <Text style={styles.cardValue}>
                              {industry.yearsOfExperience.years} Years
                            </Text>
                          )}
                          <Text style={styles.cardSubtext}>Industry Experience</Text>
                        </View>
                      ))}
                    </>
                  )}
                </View>
              </View>
            )}

          {/* Enhanced Work History with better page break handling */}
          {resumeData.jobHistory && resumeData.jobHistory.length > 0 && (
            <View style={styles.section}>
              <View style={styles.sectionHeader} wrap={false}>
                <View style={styles.sectionIcon}>
                  <SectionIcon name="work" />
                </View>
                <Text style={styles.sectionTitle}>Work History</Text>
              </View>
              {resumeData.jobHistory.map((job) => (
                <View
                  key={`${job.jobTitle}-${job.companyName}-${job.id || job.startDate}`}
                  style={styles.jobContainer}
                  wrap={false}
                >
                  <View style={styles.jobHeader}>
                    <View style={styles.jobTitleSection}>
                      <View style={styles.jobTitleRow}>
                        <View style={styles.jobIcon} />
                        <Text style={styles.jobTitle}>{job.jobTitle || "Position"}</Text>
                      </View>
                      <View style={styles.companyRow}>
                        <Text style={styles.companyText}>{job.companyName}</Text>
                        {(job.city || job.state) && (
                          <Text style={styles.locationText}>
                            • {[job.city, job.state].filter(Boolean).join(", ")}
                          </Text>
                        )}
                      </View>
                    </View>
                    <View style={styles.dateContainer}>
                      <Text style={styles.dateText}>
                        {job.startDate ? moment(job.startDate).format("MMM YYYY") : ""}
                        {job.startDate ? " - " : ""}
                        {job.endDate ? moment(job.endDate).format("MMM YYYY") : "Present"}
                      </Text>
                    </View>
                  </View>

                  {job.responsibilities && job.responsibilities.length > 0 && (
                    <View style={styles.responsibilitiesList}>
                      {job.responsibilities.map((resp) => (
                        <View
                          key={`resp-${job.id || job.startDate}-${resp.id || resp.description}`}
                          style={styles.responsibilityItem}
                        >
                          <View style={styles.bullet} />
                          <Text style={styles.responsibilityText}>
                            {typeof resp === "string" ? resp : resp.description}
                          </Text>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              ))}
            </View>
          )}

          {/* Enhanced Education Section */}
          {resumeData.education && resumeData.education.length > 0 && (
            <View style={styles.section}>
              <View style={styles.sectionHeader} wrap={false}>
                <View style={styles.sectionIcon}>
                  <SectionIcon name="education" />
                </View>
                <Text style={styles.sectionTitle}>Education</Text>
              </View>
              {resumeData.education.map((edu) => (
                <View
                  key={`${edu.degree}-${edu.institutionName}-${edu.startDate}`}
                  style={styles.educationContainer}
                  wrap={false}
                >
                  <View style={styles.educationHeader}>
                    <View style={styles.educationContent}>
                      <View style={styles.educationIcon} />
                      <View>
                        <Text style={styles.degreeTitle}>{edu.degree}</Text>
                        <Text style={styles.institutionText}>
                          {edu.institutionName}
                          {(edu.city || edu.state) &&
                            ` • ${[edu.city, edu.state].filter(Boolean).join(", ")}`}
                        </Text>
                        {edu.fieldOfStudy && (
                          <Text style={styles.institutionText}>
                            Major: {edu.fieldOfStudy}
                            {edu.minor && `, Minor: ${edu.minor}`}
                          </Text>
                        )}
                      </View>
                    </View>
                    <View style={styles.dateContainer}>
                      <Text style={styles.dateText}>
                        {edu.startDate ? moment(edu.startDate).format("MMM YYYY") : ""}
                        {edu.startDate ? " - " : ""}
                        {edu.endDate ? moment(edu.endDate).format("MMM YYYY") : "Present"}
                      </Text>
                    </View>
                  </View>
                </View>
              ))}
            </View>
          )}

          {/* Enhanced Skills Section */}
          {resumeData.skillsData &&
            (resumeData.skillsData.tags?.length > 0 ||
              resumeData.skillsData.otherSkills?.length > 0) && (
              <View style={styles.section} wrap={false}>
                <View style={styles.sectionHeader}>
                  <View style={styles.sectionIcon}>
                    <SectionIcon name="skills" />
                  </View>
                  <Text style={styles.sectionTitle}>Skills</Text>
                </View>
                <View style={styles.skillsContainer} wrap={false}>
                  {resumeData.skillsData.tags?.map((skill) => (
                    <Text key={`skill-tag-${skill}`} style={styles.tagChip}>
                      {skill}
                    </Text>
                  ))}
                  {resumeData.skillsData.otherSkills?.map((skill) => (
                    <Text key={`skill-${skill}`} style={styles.skillChip}>
                      {skill}
                    </Text>
                  ))}
                </View>
              </View>
            )}

          {/* Enhanced Languages Section */}
          {resumeData.languages && resumeData.languages.length > 0 && (
            <View style={styles.section} wrap={false}>
              <View style={styles.sectionHeader}>
                <View style={styles.sectionIcon}>
                  <SectionIcon name="languages" />
                </View>
                <Text style={styles.sectionTitle}>Languages</Text>
              </View>
              <View style={styles.skillsContainer}>
                {resumeData.languages.map((language) => (
                  <Text key={`lang-${language}`} style={styles.languageChip}>
                    {language}
                  </Text>
                ))}
              </View>
            </View>
          )}

          {/* Enhanced Certifications Section */}
          {resumeData.certifications && resumeData.certifications.length > 0 && (
            <View style={styles.section}>
              <View style={styles.sectionHeader} wrap={false}>
                <View style={styles.sectionIcon}>
                  <SectionIcon name="certifications" />
                </View>
                <Text style={styles.sectionTitle}>Certifications</Text>
              </View>
              {resumeData.certifications.map((cert) => (
                <View
                  key={`cert-${cert.name}-${cert.issuer}-${cert.year}`}
                  style={styles.certContainer}
                  wrap={false}
                >
                  <Text style={styles.certTitle}>{cert.name}</Text>
                  {cert.issuer && <Text style={styles.certDetails}>Issued by: {cert.issuer}</Text>}
                  {Number(cert.year) > 0 && moment(cert.year, "YYYY").isValid() && (
                    <Text style={styles.certDetails}>Year: {cert.year}</Text>
                  )}
                  {cert.description && <Text style={styles.certDetails}>{cert.description}</Text>}
                  {cert.credentialId && (
                    <Text style={styles.certDetails}>ID: {cert.credentialId}</Text>
                  )}
                  {cert.issueDate && (
                    <Text style={styles.certDetails}>
                      Issue Date: {moment(cert.issueDate).format("MMM YYYY")}
                    </Text>
                  )}
                </View>
              ))}
            </View>
          )}

          {resumeData.licenses && resumeData.licenses.length > 0 && (
            <LicensesSection licenses={resumeData.licenses} />
          )}

          {/* Enhanced Portfolio Section */}
          {resumeData.portfolio && resumeData.portfolio.length > 0 && (
            <View style={styles.section}>
              <View style={styles.sectionHeader} wrap={false}>
                <View style={styles.sectionIcon}>
                  <SectionIcon name="portfolio" />
                </View>
                <Text style={styles.sectionTitle}>Portfolio</Text>
              </View>
              {resumeData.portfolio.map((project) => (
                <View
                  key={`project-${project.title}-${project.url}`}
                  style={styles.portfolioContainer}
                  wrap={false}
                >
                  <Text style={styles.portfolioTitle}>{project.title}</Text>
                  {project.description && (
                    <Text style={styles.certDetails}>{project.description}</Text>
                  )}
                  {project.url && (
                    <Link
                      href={formatPdfUrl(project.url)}
                      src={formatPdfUrl(project.url)}
                      style={styles.portfolioLink}
                    >
                      View Project
                    </Link>
                  )}
                </View>
              ))}
            </View>
          )}

          {/* 🏆 Awards Section */}
          {resumeData.awards && resumeData.awards.length > 0 && (
            <AwardsSection awards={resumeData.awards} />
          )}
          {/* Enhanced References Section */}
          {resumeData.references && resumeData.references.length > 0 && (
            <View style={styles.section}>
              <View style={styles.sectionHeader} wrap={false}>
                <View style={styles.sectionIcon}>
                  <SectionIcon name="references" />
                </View>
                <Text style={styles.sectionTitle}>References</Text>
              </View>
              {resumeData.references.map((reference) => (
                <View
                  key={`reference-${reference.firstName}-${reference.lastName}-${reference.company}`}
                  style={styles.referenceContainer}
                  wrap={false}
                >
                  <Text style={styles.referenceName}>
                    {[reference.firstName, reference.lastName].filter(Boolean).join(" ")}
                  </Text>
                  {reference.company && (
                    <Text style={styles.referenceDetails}>{reference.company}</Text>
                  )}
                  {reference.job && <Text style={styles.referenceDetails}>{reference.job}</Text>}
                  {reference.email && (
                    <Text style={styles.referenceDetails}>{reference.email}</Text>
                  )}
                  {reference.phone && (
                    <Text style={styles.referenceDetails}>{reference.phone}</Text>
                  )}
                </View>
              ))}
            </View>
          )}
        </View>
      </Page>
    </Document>
  );
};

export const ResumeDownloadButton = ({
  resumeData,
  buttonText = "Download PDF",
  companyName,
  companyLogo,
  disabled = false,
}) => (
  <PDFDownloadLink
    document={
      <ResumePDF resumeData={resumeData} companyName={companyName} companyLogo={companyLogo} />
    }
    fileName={`${resumeData?.contactInfo?.firstName || ""}${
      resumeData?.contactInfo?.lastName ? `_${resumeData.contactInfo.lastName}` : ""
    }_Resume.pdf`}
    style={{
      background: disabled
        ? "linear-gradient(135deg, #9E9E9E 0%, #757575 100%)"
        : "linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)",
      color: "white",
      padding: "12px 24px",
      borderRadius: "6px",
      textDecoration: "none",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "14px",
      fontWeight: "600",
      cursor: disabled ? "not-allowed" : "pointer",
      boxShadow: disabled ? "0 1px 3px rgba(0, 0, 0, 0.1)" : "0 3px 12px rgba(74, 144, 226, 0.3)",
      transition: "all 0.3s ease",
      opacity: disabled ? 0.6 : 1,
      pointerEvents: disabled ? "none" : "auto",
    }}
  >
    {({ blob, url, loading, error }) => (loading ? "Generating PDF..." : buttonText)}
  </PDFDownloadLink>
);
